import { useMesCommandes } from '@/hooks/useCommercial';
import { Badge, type BadgeTone } from './_ui';
import type { Proforma } from '@/api/commercial';
import { Truck, Package, Factory, CheckCircle2, Send } from 'lucide-react';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Suivi des commandes en cours.
 *
 * Vue orientée « où en est ma commande ? » avec une timeline visuelle des
 * étapes clefs du workflow béton :
 *   confirmee → en_fabrication → prete_au_parc → expediee → livree → facturee
 */

const ETAPES: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: 'confirmee',      label: 'Confirmée',      icon: <CheckCircle2 size={14} /> },
  { key: 'en_fabrication', label: 'En fabrication', icon: <Factory size={14} /> },
  { key: 'prete_au_parc',  label: 'Prête au parc',  icon: <Package size={14} /> },
  { key: 'expediee',       label: 'Expédiée',       icon: <Send size={14} /> },
  { key: 'livree',         label: 'Livrée',         icon: <Truck size={14} /> },
];

const STATUS_LABELS: Record<string, { label: string; tone: BadgeTone }> = {
  confirmee:      { label: 'Confirmée',      tone: 'info' },
  en_fabrication: { label: 'En fabrication', tone: 'info' },
  prete_au_parc:  { label: 'Prête au parc',  tone: 'info' },
  expediee:       { label: 'Expédiée',       tone: 'info' },
  livree:         { label: 'Livrée',         tone: 'success' },
  facturee:       { label: 'Facturée',       tone: 'success' },
};

export const MesCommandesPage = () => {
  const { data = [], isLoading } = useMesCommandes();

  return (
    <div>
      <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>Mes commandes</h1>
      <p style={{ margin: '0 0 16px', color: 'var(--tx-3, #6B7280)', fontSize: 13 }}>
        Toutes tes commandes confirmées, en cours ou livrées
      </p>

      {isLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>}

      {!isLoading && data.length === 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--bd-1)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
          <Truck size={40} style={{ color: 'var(--tx-3)', marginBottom: 8 }} />
          <p style={{ color: 'var(--tx-3, #6B7280)', margin: 0, fontSize: 14 }}>
            Aucune commande en cours. Fais valider ta première proforma par le DAF.
          </p>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {data.map((c) => (
          <CommandeCard key={c.id} commande={c} />
        ))}
      </div>
    </div>
  );
};

function CommandeCard({ commande: c }: { commande: Proforma }) {
  const stat  = STATUS_LABELS[c.statut] ?? { label: c.statut, tone: 'neutral' as const };
  const etape = ETAPES.findIndex((e) => e.key === c.statut);
  const ht    = Number(c.montantHtFcfa);

  const jourLivraison = c.dateLivraisonSouhaitee ? new Date(c.dateLivraisonSouhaitee) : null;

  return (
    <div style={{
      background: '#fff', border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 12,
      padding: 14, display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{c.reference}</div>
          <div style={{ fontSize: 13, color: 'var(--tx-2)' }}>
            {c.client?.fullName ?? '—'}
            {c.chantier?.nom && <span style={{ color: 'var(--tx-3, #6B7280)' }}> · {c.chantier.nom}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 2 }}>
            Confirmée le {new Date(c.valideeLe ?? c.updatedAt).toLocaleDateString('fr-FR')}
          </div>
        </div>
        <Badge tone={stat.tone}>{stat.label}</Badge>
      </div>

      {/* Timeline mobile — les étapes actives au-dessous et à gauche */}
      <div style={{
        display: 'grid', gridTemplateColumns: `repeat(${ETAPES.length}, 1fr)`,
        gap: 4, background: 'var(--bg-1, #FAFAFA)', padding: 8, borderRadius: 8,
      }}>
        {ETAPES.map((e, i) => {
          const done = etape >= 0 && i <= etape;
          return (
            <div key={e.key} style={{ textAlign: 'center', minWidth: 0 }}>
              <div style={{
                width: 26, height: 26, borderRadius: 13, margin: '0 auto',
                background: done ? '#10B981' : 'var(--bd-1, #E5E7EB)',
                color: done ? '#fff' : 'var(--tx-3, #6B7280)',
                display: 'grid', placeItems: 'center',
              }}>
                {e.icon}
              </div>
              <div style={{
                fontSize: 10, marginTop: 4, fontWeight: done ? 600 : 400,
                color: done ? 'var(--tx-1, #111827)' : 'var(--tx-3, #6B7280)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {e.label}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>
          {jourLivraison && (
            <>Livraison prévue <strong>{jourLivraison.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</strong></>
          )}
        </div>
        <div style={{ fontWeight: 800, fontSize: 16, fontFamily: 'monospace' }}>
          {ht.toLocaleString('fr-FR')} FCFA
        </div>
      </div>
    </div>
  );
}
